import './styles/community.css';
import './styles/ops.css';
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loadMe } from './actions/authActions';

import Header from './components/Header';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvite from './pages/AcceptInvite';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import AdminUsers from './pages/admin/Users';
import AdminInvites from './pages/admin/Invites';
import AdminRoles from './pages/admin/Roles';
import ApiKeys from './pages/account/ApiKeys';

import Bookings from './pages/ops/Bookings';
import BookingDetail from './pages/ops/BookingDetail';
import Quotes from './pages/ops/Quotes';
import QuoteDetail from './pages/ops/QuoteDetail';
import Services from './pages/ops/Services';
import Teams from './pages/ops/Teams';
import ReviewsPage from './pages/ops/Reviews';
import Coverage from './pages/ops/Coverage';

import Questions from './pages/Questions';
import QuestionDetail from './pages/QuestionDetail';
import AskQuestion from './pages/AskQuestion';
import Articles from './pages/Articles';
import ArticleDetail from './pages/ArticleDetail';
import WriteArticle from './pages/WriteArticle';

// Staff roles for this business. `cleaner` is included wherever a team member
// legitimately needs the screen (their jobs, the checklist) and excluded from the
// rest — the capability checks on the server are the real gate; this only decides
// what is worth rendering.
const OPS = ['cleaner', 'dispatcher', 'manager', 'admin'];
const OFFICE = ['dispatcher', 'manager', 'admin'];
const MANAGE = ['manager', 'admin'];

function ProtectedRoute({ children, roles }) {
  const { user } = useSelector((s) => s.auth);
  // While loadMe() resolves the persisted token, render a loading shell instead of
  // bouncing to /login — the token's presence tells us a session is in flight.
  const hasPersistedToken = typeof window !== 'undefined' && !!localStorage.getItem('auth_token');
  if (!user) {
    if (hasPersistedToken) return <div className="section narrow"><p className="muted">Loading…</p></div>;
    return <Navigate to="/login" replace />;
  }
  if (roles && !roles.includes(user.role) && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const dispatch = useDispatch();
  useEffect(() => { dispatch(loadMe()); }, [dispatch]);
  return (
    <>
      <Header />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Home />} />

          <Route path="/ops/bookings" element={<ProtectedRoute roles={OPS}><Bookings /></ProtectedRoute>} />
          <Route path="/ops/bookings/:ref" element={<ProtectedRoute roles={OPS}><BookingDetail /></ProtectedRoute>} />
          <Route path="/ops/quotes" element={<ProtectedRoute roles={OFFICE}><Quotes /></ProtectedRoute>} />
          <Route path="/ops/quotes/:ref" element={<ProtectedRoute roles={OFFICE}><QuoteDetail /></ProtectedRoute>} />
          <Route path="/ops/services" element={<ProtectedRoute roles={MANAGE}><Services /></ProtectedRoute>} />
          <Route path="/ops/teams" element={<ProtectedRoute roles={OFFICE}><Teams /></ProtectedRoute>} />
          <Route path="/ops/reviews" element={<ProtectedRoute roles={OFFICE}><ReviewsPage /></ProtectedRoute>} />
          <Route path="/ops/coverage" element={<ProtectedRoute roles={MANAGE}><Coverage /></ProtectedRoute>} />

          <Route path="/questions" element={<Questions />} />
          <Route path="/questions/ask" element={<AskQuestion />} />
          <Route path="/questions/:slug" element={<QuestionDetail />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/new" element={<WriteArticle />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />

          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/account/api-keys" element={<ProtectedRoute><ApiKeys /></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute roles={MANAGE}><Admin /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={MANAGE}><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/invites" element={<ProtectedRoute roles={MANAGE}><AdminInvites /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute roles={['admin']}><AdminRoles /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="app-footer">
        <p>Squeakky — House of Service · operations console. <a href="https://squeakky.com">squeakky.com</a></p>
      </footer>
    </>
  );
}
